import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { getVisitTimelinessReport } from "../../../../services/features/reports.service";
import {
  KpiRow,
  KpiTile,
  ReportGrid,
  ChartCard,
  EmptyNote,
  ReportBar,
  ReportDonut,
  SERIES,
  BRAND,
  OK_COLOR,
  LATE_COLOR,
  num,
  pct,
  barData,
  barOptions,
  donutData,
  donutOptions,
  allZero,
} from "./reportShared";

// Report 2 — Deadline & Timeliness. Were visits completed within the planned
// date, and how large is the average delay?
const TimelinessReport = ({ onReady, onLoadingChange }) => {
  const projectId = localStorage.getItem("currentProjectID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    // Mirror the Visit Data Summary call: span the contract window when known,
    // otherwise fall back to the current month.
    const cStart = localStorage.getItem("Contract-startDate");
    const cEnd = localStorage.getItem("Contract-endDate");
    getVisitTimelinessReport(
      {
        projectId,
        startDate: (cStart ? dayjs(cStart) : dayjs()).startOf("month").toISOString(),
        endDate: (cEnd ? dayjs(cEnd) : dayjs()).endOf("month").toISOString(),
      },
      (res) => {
        setData(res?.data || null);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectId]);

  const planned = num(data?.visitPlanned);
  const completed = num(data?.visitCompleted);
  const onTime = num(data?.completedWithinDate);
  const late = num(data?.notCompletedWithinDate);
  const delayDays = num(data?.delayDays);
  const onTimeRate = pct(onTime, onTime + late);
  const completionRate = pct(completed, planned);

  const kpis = useMemo(
    () => [
      { label: "On-time rate", value: onTimeRate, suffix: "%", color: OK_COLOR },
      { label: "Completed on time", value: onTime, color: OK_COLOR },
      { label: "Late / overdue", value: late, color: LATE_COLOR },
      { label: "Avg delay", value: delayDays, suffix: " d", color: BRAND },
      { label: "Completion", value: completionRate, suffix: "%", color: SERIES[1] },
    ],
    [onTimeRate, onTime, late, delayDays, completionRate]
  );

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  useEffect(() => {
    if (!loading) onReady?.(kpis);
  }, [loading, kpis, onReady]);

  if (loading) return null;

  const timeRows = [
    { label: "On time", value: onTime, color: OK_COLOR },
    { label: "Late", value: late, color: LATE_COLOR },
  ];
  const planRows = [
    { label: "Planned", value: planned, color: SERIES[1] },
    { label: "Completed", value: completed, color: OK_COLOR },
  ];

  return (
    <>
      <KpiRow>
        {kpis.map((k) => (
          <KpiTile key={k.label} {...k} />
        ))}
      </KpiRow>

      <ReportGrid>
        <ChartCard title="On-time vs late" hint="Against planned date">
          {allZero(timeRows) ? (
            <EmptyNote />
          ) : (
            <ReportDonut data={donutData(timeRows)} options={donutOptions} />
          )}
        </ChartCard>

        <ChartCard title="Planned vs completed">
          {allZero(planRows) ? (
            <EmptyNote />
          ) : (
            <ReportBar data={barData(planRows, SERIES)} options={barOptions(true)} />
          )}
        </ChartCard>

        <ChartCard title="Average delay">
          <div className="reportBigStat">
            <span className="reportBigStatValue" style={{ color: BRAND }}>
              {delayDays}
            </span>
            <span className="reportBigStatUnit">days</span>
            <span className="reportBigStatCaption">
              Average gap between estimated start and end per site
            </span>
          </div>
        </ChartCard>
      </ReportGrid>
    </>
  );
};

export default TimelinessReport;
